import * as Placeholder from '-/Custom/Placeholder';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useRef } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import BadgeListItem, {
  BadgeListItemFragment,
} from '@/components/Badge/BadgeListItem';
import Paginator from '@/components/Custom/Paginator';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import { graphql, readFragment } from '@/gql/graphql';
import usePaginatedList from '@/hooks/usePaginatedList';

const BADGE_PAGE_LIMIT = 24;

const GetTeamBadgeList = graphql(/* GraphQL */ `
  query GetTeamBadgeList(
    $teamId: uuid!
    $badgeAssigneesWhere: badge_assignee_bool_exp!
    $includeBadgeAssignees: Boolean!
    $badgeLimit: Int!
    $badgeOffset: Int!
  ) {
    team_by_pk(id: $teamId) {
      id
      title
      permissions
    }
    relevant_badge(
      args: { team_id: $teamId }
      limit: $badgeLimit
      offset: $badgeOffset
      order_by: { title: asc }
    ) {
      id
      ...BadgeListItem
    }
    relevant_badge_aggregate(args: { team_id: $teamId }) {
      aggregate {
        count
      }
    }
  }
`, [BadgeListItemFragment]);

export default function TeamDetailBadge({
  badgePathname = '/team/badge/[badgeId]',
}: {
  badgePathname?:
    | '/team/badge/[badgeId]'
    | '/organization/badge/[badgeId]'
    | '/event/badge/[badgeId]'
    | '/chat/badge/[badgeId]';
}) {
  const { push } = useRouter();
  const { personId, teamId } = useLocalSearchParams<{
    personId?: string;
    teamId: string;
  }>();
  const scrollViewRef = useRef<ScrollView>(null);
  const paginated = usePaginatedList({
    limit: BADGE_PAGE_LIMIT,
    scrollViewRef,
  });
  const badgeAssigneesWhere = {
    team_id: { _eq: teamId },
    ...(personId ? { person_id: { _eq: personId } } : {}),
  };
  const [{ data, fetching }] = useQuery({
    query: GetTeamBadgeList,
    variables: {
      teamId: teamId || '',
      badgeAssigneesWhere,
      includeBadgeAssignees: true,
      badgeLimit: paginated.limit,
      badgeOffset: paginated.offset,
    },
    pause: !teamId,
  });

  const team = data?.team_by_pk;
  const badges = data?.relevant_badge ?? [];
  const badgeCount = data?.relevant_badge_aggregate.aggregate?.count ?? 0;

  return (
    <FadeInContent fetching={fetching}>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.content}
        contentInsetAdjustmentBehavior="automatic"
      >
        {!personId && team?.permissions?.edit_badge ? (
          <Box direction="horizontal" space="md" py="md" px="lg">
            <Link
              href={{
                pathname: '/badge/create',
                params: { teamId },
              }}
              asChild
            >
              <ActionButton
                forceLabel
                label="Create Badge"
                icon="add-outline"
                variant="success"
              />
            </Link>
          </Box>
        ) : null}

        {badges.length ? (
          <Box direction="vertical" testID="TeamBadgeList">
            {badges.map((badge) => {
              const badgeItem = readFragment(BadgeListItemFragment, badge);

              return (
                <Item
                  key={badgeItem.id}
                  testID={`TeamBadgeListItem-${badgeItem.id}`}
                  onPress={() => {
                    push({
                      pathname: badgePathname,
                      params: { badgeId: badgeItem.id, personId, teamId },
                    });
                  }}
                >
                  <BadgeListItem fragment={badge} />
                </Item>
              );
            })}
          </Box>
        ) : (
          <Placeholder.Container>
            <Box style={styles.placeholderIcon}>
              <Placeholder.Icon name="grid-outline" />
            </Box>
            <Placeholder.Text>{team?.title} Has No Badges.</Placeholder.Text>
            <Placeholder.Text>
              Team badges can be used to track registration, waivers, and roster
              details.
            </Placeholder.Text>
          </Placeholder.Container>
        )}

        <Paginator {...paginated} total={badgeCount} />
      </ScrollView>
    </FadeInContent>
  );
}

const styles = StyleSheet.create((theme) => ({
  content: {
    flexGrow: 1,
    gap: theme.space('lg'),
    paddingBottom: theme.space('xl'),
  },
  placeholderIcon: {
    transform: [{ rotate: '45deg' }],
  },
}));
